// The words the whole product says about itself, in one place.
//
// Three surfaces make the same three claims — what this is, what a plan buys,
// and what the extension does — and until now each wrote its own version. They
// drifted: the pricing page sold "applications", the Account page spoke in
// tokens, and the extension promised it would never press Submit while
// Auto-apply was pressing Submit. A person who reads two of them and gets two
// answers stops believing either.
//
// So: one file, imported by Pricing, the landing page and Account. The
// extension cannot import from here (it is plain files with no build step), so
// copy.test.js opens the popup the manifest names (extension/popup/popup.html)
// the way Chrome does, reads manifest.json, and fails when what they say stops
// matching WHAT_THE_EXTENSION_DOES below.
//
// Rules for editing: say it the way a person would say it out loud. No word a
// job-seeker would have to learn (no tokens, credits, models, stages, ATS). If
// a claim here stops being true in the product, change the product or change
// the claim — never leave them apart.

/**
 * The name, spelled one way: one word, as the domain spells it (founder,
 * 2026-10-19). Every brand mark, footer, page title and og:site_name on the
 * public pages reads it from here.
 */
export const BRAND = 'AssistAlien';

/** The address the site gives for itself (index.html's canonical link). */
export const SITE_URL = 'https://www.assistalien.com';

/**
 * The positioning (founder, 2026-10-19): the headline, and the line under it.
 * The landing page's hero, the footer and the pricing page say these; the
 * browser tab, Google's result and the LinkedIn or Slack card say them too,
 * through SITE_TITLE and SITE_DESCRIPTION below. It was "A job hunt you can
 * talk to." / "Say what you want, or click and do it yourself."
 *
 * To change the positioning, change these two lines. Everything that renders
 * follows; public/index.html cannot import this file, so seo.test.js fails
 * until its title, description and card tags are updated to match.
 */
export const IDENTITY = 'Your job search. One conversation.';
export const HOW = 'Tell it what you want. Find roles, shape your resume, and apply—all in one place.';

/** The tab, the search result headline, the card title. */
export const SITE_TITLE = `${BRAND} | ${IDENTITY.replace(/\.$/, '')}`;
/** The search result's description and the card's (under 160 characters). */
export const SITE_DESCRIPTION =
  `Find jobs, tailor your resume, and fill applications in your browser with ${BRAND}. Review and submit, or enable Auto-apply for supported jobs.`;

/**
 * Five things that are true and checkable, in plain words, shown in a strip
 * under the landing page's hero (founder, 2026-10-19). No "only" and no
 * "first": each one is a thing the product does, not a ranking against others.
 *   - one conversation: the chat runs every page (Shell/ChatShell.js);
 *   - employers' own systems: the crawler reads 26 hiring systems
 *     (Node Backend/services/jobSearch/providers), not job boards' copies;
 *   - the real application: WHAT_THE_EXTENSION_DOES;
 *   - a resume per role: variants (Design Studio);
 *   - visa history: JobCard.js hasH1bSponsorHistory, from USCIS records.
 */
export const DIFFERENTIATORS = [
  'One conversation',
  "Jobs from employer career pages",
  'Applications in your browser',
  'Resumes tailored to the role',
  "Employer visa history",
];

/** The one thing a paid plan buys. Everything else is free on every plan. */
export const WHAT_A_PLAN_BUYS = 'applications submitted for you';

/**
 * What the extension does — the claim the Chrome Web Store listing, the popup
 * and the pricing page must all make. Note the second half: it presses Submit
 * ONLY when the person has turned Auto-apply on. The old wording ("this
 * extension never will") was written before that existed and became a lie.
 */
export const WHAT_THE_EXTENSION_DOES =
  'Fills the employer\'s real page in your own browser. You press Submit, or let it.';

/**
 * Where the extension is downloaded from, until the Chrome Web Store listing
 * is live (extension/store/LISTING.md): the zip in public/, unzipped and
 * loaded with "Load unpacked". The Applications page's install card
 * (JobAgent.js) and the public /extension page both link it;
 * ExtensionPage.test.js fails if they stop naming the same file.
 */
export const EXTENSION_ZIP = '/assistalien-extension.zip';

/**
 * The hiring sites the extension really fills: a passing real page in the
 * conformance corpus (React Frontend/conformance/corpus.json, scores in
 * docs/ops/corpus.md), or for Workday and SmartRecruiters a lab page and
 * tests of their own. Left off until they have that: iCIMS and Taleo (never
 * tried, the form sits behind a session), Teamtailor (half its fields).
 * The /extension page lists them and the landing page counts them, from here,
 * so the two cannot disagree.
 */
export const EXTENSION_SITES = [
  'Greenhouse', 'Lever', 'Ashby', 'Workday', 'SmartRecruiters', 'Workable', 'Recruitee',
  'Jobvite', 'BambooHR', 'JazzHR', 'Rippling', 'Breezy', 'Pinpoint',
];

/**
 * The landing page's three numbers. Each is checkable: the job count is the
 * catalogue's (US, open, about 889,000 on 2026-10-15); the sites are the list
 * above, counted; "0 answers made up" is the fill's rule (the AI may only pick
 * the form's own options, verbatim; legal and sponsorship answers come only
 * from the person's own; a draft is written from their resume).
 */
export const STATS = [
  { value: 900, suffix: 'K+', label: 'open jobs' },
  { value: EXTENSION_SITES.length, suffix: '', label: 'hiring sites filled' },
  { value: 0, suffix: '', label: 'answers made up' },
];

/**
 * How the extension works, in three steps, as the toolbar popup says them
 * with nothing running (extension/popup/popup.html; the founder, 2026-10-31:
 * "make the UI for extension easy and understandable"). The Applications
 * page's own words: its Ready card says "Click Apply. We fill it in. You
 * click Submit.", its other cards "Click Open.", and the switch on Today is
 * Auto-apply. copy.test.js opens the popup and reads each step as shown.
 */
export const EXTENSION_STEPS = [
  'Open your Applications page.',
  'Click Apply or Open on a job. We fill it in.',
  'You click Submit. Or turn on Auto-apply and we send it.',
];

/** Said wherever a person might fear the count applies to their own clicks. */
export const FILLING_IS_FREE =
  'Filling forms yourself is unlimited on every plan. Only a Submit it presses for you is counted.';

/**
 * What the Tracker is: the record of what went out, fed by the extension
 * seeing each confirmation page, with the resume and cover letter sealed at
 * the moment of sending (JobTracker.js). It reads no mail.
 */
export const TRACKER_LINE = 'Every application that went out, with the resume and cover letter the employer got.';

/**
 * How much AI a plan includes (founder, 2026-10-30: "plans should feel like
 * ChatGPT or Claude: usable across the month, honest, no 'unlimited'").
 *
 * Each plan has a month of AI behind it (services/aiUsage.js
 * PLAN_BUDGET_USD on the server; the amounts are never shown). Pro has 7
 * times Free's and Max 19 times; Pricing.test.js reads the server's numbers
 * and fails when these stop being their ratio. On top of the month, any 5
 * hours may use up to 8% of it and any 7 days up to 35%, so nobody uses a
 * month up in an afternoon. Auto, the default, routes each turn to the model
 * that fits it and counts 20% less.
 *
 * The Account page says the windows sentence under its three bars; Pricing
 * says all of it.
 */
export const AI_MULTIPLE = { pro: 7, power: 19 };
export const AI_WINDOWS_LINE =
  'Any 5 hours may use up to 8% of the month, and any 7 days up to 35%, so a plan lasts the whole month instead of a day.';
export const AUTO_LINE =
  'Auto, the default, picks the best model for each task and uses 20% less of your plan.';
/** What the chat says when a window is used, and when a model's share runs short (the server's words). */
export const WINDOW_USED_EXAMPLE = "You've used this window. Back at 3:10 pm.";
export const LANDING_EXAMPLE = 'Using the faster model until 3:10 pm.';

/** The six parts, in the order of a job hunt. One line each, no jargon. */
export const PARTS = [
  { part: 'Chat', line: 'Say what you want. It does it, on the page beside you.' },
  { part: 'Design Studio', line: 'Import a PDF or start blank. A real resume you can edit, or change by saying so.' },
  { part: 'Jobs', line: 'Hundreds of thousands of open jobs, ranked against your resume, with the reason on each card.' },
  { part: 'Browser extension', line: WHAT_THE_EXTENSION_DOES },
  { part: 'Tracker', line: TRACKER_LINE },
  { part: 'Interview coach', line: 'Your stories, a cheat sheet for the role, and practice that talks back.' },
];

/**
 * What each plan is for. The counts live in the backend registry
 * (services/limits.js DOORS.applications) and reach the page through
 * Pricing.js — never hard-code a number here.
 */
export const PLAN_LEAD = {
  free: 'Try the whole thing',
  pro: 'For a real job hunt',
  power: 'Get the most out of it',
};
